import { Chip } from "@material-ui/core";
import { FormHelperTextProps } from "@material-ui/core/FormHelperText";
import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import TextField from "@material-ui/core/TextField";
import { Clear as ClearIcon } from "@material-ui/icons";
import Debounce, { DebounceProps } from "@saleor/components/Debounce";
import { FetchMoreProps } from "@saleor/types";
import Downshift, { ControllerStateAndHelpers } from "downshift";
import { filter } from "fuzzaldrin";
import React from "react";

import MultiChipsSelectFieldContent, {
  MultiChipsActionType,
  MultiChipsChoiceType
} from "./MultiChipsSelectFieldContent";

const useStyles = makeStyles(
  theme => ({
    chip: {
      backgroundColor: "#accaff",
      borderRadius: "6px",
      color: "#0B337B",
      marginRight: "5px",
      marginTop: "8px",
      ...{
        "& svg": {
          backgroundColor: "#accaff",
          color: "#0B337B",
          transform: "scale(0.8)"
        }
      }
    },
    chipClose: {
      "&:hover": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      height: 32,
      padding: 0,
      width: 32
    },
    chipContainer: {
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1)
    },
    chipInner: {
      "& svg": {
        color: theme.palette.primary.contrastText
      },
      alignItems: "center",
      background: fade(theme.palette.primary.main, 0.8),
      borderRadius: 18,
      color: theme.palette.primary.contrastText,
      display: "flex",
      justifyContent: "space-between",
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1)
    },
    chipLabel: {
      color: theme.palette.primary.contrastText
    },
    container: {
      flexGrow: 1,
      position: "relative",
      ...{
        "& .MuiInputBase-root": {
          display: "inline-block",
          paddingBottom: 0
        }
      },
      ...{
        "& MuiOutlinedInput-inputMultiline": {
          padding: "10px 0 0 0 !important"
        }
      }
    },
    disabledChipInner: {
      "& svg": {
        color: theme.palette.grey[200]
      },
      alignItems: "center",
      background: fade(theme.palette.grey[400], 0.8),
      borderRadius: 18,
      color: theme.palette.primary.contrastText,
      display: "flex",
      justifyContent: "space-between",
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1)
    },
    hideCloseIcon: {
      display: "none"
    }
  }),
  { name: "MultiChipsSelectField" }
);

export interface MultiChipsSelectFieldProps extends Partial<FetchMoreProps> {
  add?: MultiChipsActionType;
  disabled?: boolean;
  manualAdd?: () => void;
  allowCustomValues?: boolean;
  displayValues: MultiChipsChoiceType[];
  error?: boolean;
  name: string;
  choices: MultiChipsChoiceType[];
  value: string[];
  limit?: number;
  loading?: boolean;
  placeholder?: string;
  helperText?: string | React.ReactNode;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  label?: string;
  fetchChoices?: (value: string) => void;
  onChange: (event: React.ChangeEvent<any>) => void;
  onKeyPress: (event: React.ChangeEvent<any>) => void;
  onRemove: (event: React.ChangeEvent<any>) => void;
  onChangeText: (text: string) => void;
}

const DebounceAutocomplete: React.ComponentType<DebounceProps<
  string
>> = Debounce;

const MultiChipsSelectFieldComponent: React.FC<MultiChipsSelectFieldProps> = props => {
  const {
    add,
    disabled,
    manualAdd,
    allowCustomValues,
    choices,
    displayValues = [],
    error,
    hasMore,
    helperText,
    formHelperTextProps,
    label,
    limit,
    loading,
    name,
    placeholder,
    value,
    fetchChoices,
    onChange,
    onFetchMore,
    onKeyPress,
    onRemove,
    onChangeText,
    ...rest
  } = props;
  const classes = useStyles(props);

  const handleSelect = (
    item: string,
    downshiftOpts?: ControllerStateAndHelpers
  ) => {
    if (downshiftOpts) {
      downshiftOpts.reset({ inputValue: "" });
    }
    onChange({
      target: { name, value: item }
    } as any);
  };

  const handleRemove = (
    item: string,
    downshiftOpts?: ControllerStateAndHelpers
  ) => {
    if (downshiftOpts) {
      downshiftOpts.reset({ inputValue: "" });
    }
    onRemove({
      target: { name, value: item }
    } as any);
  };

  return (
    <>
      <Downshift
        onInputValueChange={e => {
          fetchChoices(e);
          onChangeText(e);
        }}
        onSelect={handleSelect}
        itemToString={() => ""}
      >
        {({
          reset,
          closeMenu,
          getInputProps,
          getItemProps,
          isOpen,
          toggleMenu,
          highlightedIndex,
          inputValue
        }) => {
          const displayCustomValue =
            inputValue &&
            inputValue.length > 0 &&
            allowCustomValues &&
            !choices.find(
              choice => choice.label.toLowerCase() === inputValue.toLowerCase()
            );

          return (
            <div className={classes.container} {...rest}>
              <TextField
                multiline
                disabled={disabled || false}
                InputProps={{
                  ...getInputProps({
                    placeholder
                  }),
                  //   endAdornment: (
                  //     <div>
                  //       <ArrowDropdownIcon onClick={() => toggleMenu()} />
                  //     </div>
                  //   ),
                  id: undefined,
                  onClick: toggleMenu,
                  startAdornment:
                    displayValues &&
                    displayValues.map(value => (
                      <Chip
                        deleteIcon={
                          <ClearIcon
                            className={disabled ? classes.hideCloseIcon : null}
                          />
                        }
                        className={classes.chip}
                        key={value.value}
                        label={value.label}
                        onDelete={() => handleRemove(value.value)}
                      />
                    ))
                }}
                inputProps={{
                  maxLength: 30
                }}
                onKeyPress={onKeyPress}
                onPaste={e => {
                  e.preventDefault();
                  return false;
                }}
                error={error}
                helperText={helperText}
                FormHelperTextProps={formHelperTextProps}
                label={label}
                fullWidth={true}
              />
              {isOpen && !disabled && (!!inputValue || !!choices?.length) && (
                <MultiChipsSelectFieldContent
                  add={
                    add
                      ? {
                          ...add,
                          onClick: () => {
                            add.onClick();
                            closeMenu();
                            reset();
                          }
                        }
                      : null
                  }
                  manualAdd={
                    manualAdd
                      ? () => {
                          manualAdd();
                          closeMenu();
                          reset();
                        }
                      : null
                  }
                  choices={choices?.filter(
                    choice => !value?.includes(choice.value)
                  )}
                  displayCustomValue={displayCustomValue}
                  displayValues={displayValues}
                  getItemProps={getItemProps}
                  hasMore={hasMore}
                  highlightedIndex={highlightedIndex}
                  loading={loading}
                  limit={limit}
                  inputValue={inputValue}
                  onFetchMore={onFetchMore}
                />
              )}
            </div>
          );
        }}
      </Downshift>
    </>
  );
};

const MultiChipsSelectField: React.FC<MultiChipsSelectFieldProps> = ({
  choices,
  fetchChoices,
  ...props
}) => {
  const [query, setQuery] = React.useState("");

  if (fetchChoices) {
    return (
      <DebounceAutocomplete debounceFn={fetchChoices}>
        {debounceFn => (
          <MultiChipsSelectFieldComponent
            choices={choices}
            {...props}
            fetchChoices={debounceFn}
          />
        )}
      </DebounceAutocomplete>
    );
  }

  return (
    <MultiChipsSelectFieldComponent
      fetchChoices={q => setQuery(q || "")}
      choices={filter(choices, query, {
        key: "label"
      })}
      {...props}
    />
  );
};

MultiChipsSelectField.displayName = "MultiChipsSelectField";
export default MultiChipsSelectField;
